import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, Button, Form, InputGroup, Spinner, Table, Badge, Dropdown } from 'react-bootstrap';
import * as FaIcons from 'react-icons/fa';

const ErroresTarifariosEmpresaDetalleComponent = ({ 
    detalleErrores, 
    loading, 
    regionId, 
    regiones, 
    onRegionChange, 
    onSavePrecios, 
    savingPrecios, 
    onBack,
    hideHeaderInfo = false // Nueva prop para ocultar información del header cuando se llama desde cierre diario
}) => {
    const [erroresConPrecios, setErroresConPrecios] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [filters, setFilters] = useState({});

    // Actualizar errores cuando cambien los datos
    useEffect(() => {
        if (detalleErrores?.errores) {
            const erroresConId = detalleErrores.errores.map((error, index) => ({
                ...error,
                tempId: `error_${index}`,
                precio: ''
            }));
            setErroresConPrecios(erroresConId);
        }
    }, [detalleErrores]);

    const handlePrecioChange = (tempId, precio) => {
        setErroresConPrecios(prev => 
            prev.map(error => 
                error.tempId === tempId 
                    ? { ...error, precio: precio }
                    : error
            )
        );
    };

    const handleSaveAll = () => {
        const erroresConPreciosValidos = erroresConPrecios.filter(error => 
            error.precio && 
            !isNaN(parseFloat(error.precio)) && 
            parseFloat(error.precio) > 0
        );

        if (erroresConPreciosValidos.length === 0) {
            alert('No hay precios válidos para guardar');
            return;
        }

        onSavePrecios(erroresConPreciosValidos);
    };

    // Función para manejar el sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Función para manejar filtros
    const handleFilterChange = (column, values) => {
        setFilters(prev => ({
            ...prev,
            [column]: values
        }));
    };

    // Obtener valores únicos para filtros
    const getUniqueValues = (column) => {
        const values = erroresConPrecios.map(error => error[column]).filter(Boolean);
        return [...new Set(values)].sort();
    };

    // Aplicar filtros y sorting a los datos
    const filteredAndSortedErrors = useMemo(() => {
        let filtered = [...erroresConPrecios];

        // Aplicar filtros
        Object.keys(filters).forEach(column => {
            if (filters[column] && filters[column].length > 0) {
                filtered = filtered.filter(error => 
                    filters[column].includes(error[column])
                );
            }
        });

        // Aplicar sorting
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    }, [erroresConPrecios, filters, sortConfig]);

    // Componente para header con sorting y filtro
    const SortableFilterableHeader = ({ column, title }) => {
        const uniqueValues = getUniqueValues(column);
        const isActive = sortConfig.key === column;
        const direction = isActive ? sortConfig.direction : null;

        return (
            <th style={{ position: 'relative', minWidth: '180px', whiteSpace: 'nowrap' }}>
                <div className="d-flex align-items-center justify-content-between">
                    <span 
                        onClick={() => handleSort(column)}
                        style={{ cursor: 'pointer', flex: 1 }}
                        className="d-flex align-items-center"
                    >
                        {title}
                        {isActive && (
                            <FaIcons.FaSort 
                                className={`ms-1 ${direction === 'asc' ? 'text-primary' : 'text-secondary'}`}
                                style={{ 
                                    transform: direction === 'desc' ? 'rotate(180deg)' : 'none'
                                }}
                            />
                        )}
                        {!isActive && <FaIcons.FaSort className="ms-1 text-muted" />}
                    </span>
                    
                    <Dropdown>
                        <Dropdown.Toggle 
                            variant="link" 
                            size="sm" 
                            className="p-0 border-0 dropdown-filter-btn"
                            style={{ 
                                color: filters[column]?.length > 0 ? '#0d6efd' : '#6c757d',
                                textDecoration: 'none !important',
                                border: 'none !important',
                                backgroundColor: 'transparent !important',
                                boxShadow: 'none !important',
                                width: 'auto',
                                height: 'auto',
                                minWidth: 'auto',
                                minHeight: 'auto',
                                lineHeight: '1'
                            }}
                        >
                            <FaIcons.FaFilter />
                        </Dropdown.Toggle>

                        <Dropdown.Menu style={{ minWidth: '250px', maxHeight: '300px', overflowY: 'auto' }}>
                            <div className="p-2">
                                <div className="mb-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        onClick={() => handleFilterChange(column, [])}
                                        className="me-2"
                                    >
                                        Limpiar
                                    </Button>
                                    {/* <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        onClick={() => handleFilterChange(column, uniqueValues)}
                                    >
                                        Todos
                                    </Button> */}
                                </div>
                                {uniqueValues.map(value => (
                                    <div key={value} style={{ fontSize: '0.2rem' }}>
                                        <Form.Check
                                            type="checkbox"
                                            id={`${column}-${value}`}
                                            label={value}
                                            checked={filters[column]?.includes(value) || false}
                                            onChange={(e) => {
                                                const currentValues = filters[column] || [];
                                                const newValues = e.target.checked
                                                    ? [...currentValues, value]
                                                    : currentValues.filter(v => v !== value);
                                                handleFilterChange(column, newValues);
                                            }}
                                            className="mb-1"
                                        />
                                    </div>
                                ))}
                            </div>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </th>
        );
        };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <Spinner animation="border" />
            </div>
        );
    }

    if (!detalleErrores) {
        return (
            <div className="text-center">
                <p>No se encontraron datos de errores</p>
            </div>
        );
    }

    const { descripcion, total_errores, rango_fecha } = detalleErrores;

    return (
        <div>
            <style>
                {`
                    .dropdown-filter-btn {
                        background: transparent !important;
                        border: none !important;
                        box-shadow: none !important;
                        text-decoration: none !important;
                        width: auto !important;
                        height: auto !important;
                        min-width: auto !important;
                        min-height: auto !important;
                        line-height: 1 !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .dropdown-filter-btn:hover,
                    .dropdown-filter-btn:focus,
                    .dropdown-filter-btn:active {
                        background: transparent !important;
                        border: none !important;
                        box-shadow: none !important;
                        text-decoration: none !important;
                    }
                `}
            </style>
            {/* Header con información y controles - solo mostrar si no se oculta */}
            {!hideHeaderInfo && (
                <Row className="mb-4">
                    <Col md={12}>
                        <Card>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div>
                                        <h5>Errores de Empresa</h5>
                                        <p className="text-muted mb-2">{descripcion}</p>
                                        <Badge bg="danger">Total: {total_errores} errores</Badge>
                                        {rango_fecha && (
                                            <Badge bg="info" className="ms-2">
                                                {rango_fecha.fecha_inicio} al {rango_fecha.fecha_fin}
                                            </Badge>
                                        )}
                                    </div>
                                    <Button variant="secondary" onClick={onBack}>
                                        Volver
                                    </Button>
                                </div>

                                {/* Filtros */}
                                <Row>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Filtrar por Región</Form.Label>
                                            <Form.Select
                                                value={regionId}
                                                onChange={(e) => onRegionChange(e.target.value)}
                                            >
                                                <option value="">Todas las regiones</option>
                                                {regiones.map(region => (
                                                    <option key={region.id} value={region.id}>
                                                        {region.region}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} className="d-flex align-items-end">
                                        <Button 
                                            variant="success" 
                                            onClick={handleSaveAll}
                                            disabled={savingPrecios || erroresConPrecios.filter(e => e.precio && parseFloat(e.precio) > 0).length === 0}
                                            className="ms-auto"
                                        >
                                            {savingPrecios && <Spinner animation="border" size="sm" className="me-2" />}
                                            Guardar Precios ({erroresConPrecios.filter(e => e.precio && parseFloat(e.precio) > 0).length})
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Tabla de errores */}
            <Row>
                <Col md={12}>
                    <Card>
                        <Card.Body>
                            <div className="table-responsive">
                                <Table striped bordered hover size="sm" className="mb-0">
                                    <thead className="sticky-top bg-light">
                                        <tr>
                                            <SortableFilterableHeader column="empresa_sigla" title="Empresa" />
                                            <SortableFilterableHeader column="comuna_nombre" title="Comuna" />
                                            <SortableFilterableHeader column="servicio_nombre" title="Servicio" />
                                            {/* <SortableFilterableHeader column="total_guias_afectadas" title="Guías Afectadas" /> */}
                                            <th>Precio</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAndSortedErrors.length > 0 ? (
                                            filteredAndSortedErrors.map((error) => (
                                                <tr key={error.tempId}>
                                                    <td>
                                                        <div>
                                                            <strong>{error.empresa_sigla}</strong>
                                                            {/* <br />
                                                            <small className="text-muted">{error.empresa_nombre}</small> */}
                                                        </div>
                                                    </td>
                                                    <td>{error.comuna_nombre}</td>
                                                    <td>{error.servicio_nombre}</td>
                                                    {/* <td>
                                                        <Badge bg="warning">{error.total_guias_afectadas}</Badge>
                                                    </td> */}
                                                    <td>
                                                        <InputGroup size="sm">
                                                            {/* <InputGroup.Text>$</InputGroup.Text> */}
                                                            <Form.Control
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={error.precio}
                                                                onChange={(e) => handlePrecioChange(error.tempId, e.target.value)}
                                                                placeholder="Ingrese precio"
                                                            />
                                                        </InputGroup>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center">
                                                    No se encontraron errores con los filtros aplicados
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {filteredAndSortedErrors.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <span className="text-muted">
                                        Mostrando {filteredAndSortedErrors.length} de {erroresConPrecios.length} errores
                                    </span>
                                    <Button 
                                        variant="success" 
                                        onClick={handleSaveAll}
                                        disabled={savingPrecios || erroresConPrecios.filter(e => e.precio && parseFloat(e.precio) > 0).length === 0}
                                    >
                                        {savingPrecios && <Spinner animation="border" size="sm" className="me-2" />}
                                        Guardar Precios ({erroresConPrecios.filter(e => e.precio && parseFloat(e.precio) > 0).length})
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

ErroresTarifariosEmpresaDetalleComponent.propTypes = {
    detalleErrores: PropTypes.object,
    loading: PropTypes.bool,
    regionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    regiones: PropTypes.array,
    onRegionChange: PropTypes.func.isRequired,
    onSavePrecios: PropTypes.func.isRequired,
    savingPrecios: PropTypes.bool,
    onBack: PropTypes.func.isRequired,
    hideHeaderInfo: PropTypes.bool // Nueva prop para ocultar información del header
};

export default ErroresTarifariosEmpresaDetalleComponent;
